import { presentation, terminology } from "@/lib/presentation";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Cpu,
  KeyRound,
  Link2,
  Lock,
  Mail,
  Radar,
  ShieldCheck,
  ShieldUser,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";
import {
  ROLE_DEFINITIONS_BY_CODE,
  TEST_ACCOUNTS,
  type CanonicalRoleCode,
  type TestAccountDefinition,
} from "@hawk/shared";
import { MULTISITE_DEMO_ACCOUNTS } from "../../../../packages/shared/multisite-demo-accounts";
import hawkLogo from "@/assets/hawk_logo.svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/components/providers/AuthProvider";
import { authClient, signIn } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { queryClient } from "@/lib/queryClient";
import { resolveApiUrl } from "@/lib/api-url";
import {
  buildCleanLoginUrl,
  resolveLoginAuthError,
  type LoginAuthError,
} from "@/lib/login-auth-error";
import {
  buildLoginCompletionPath,
  buildLoginErrorPath,
  getSafeLoginReturnTo,
} from "@/lib/login-navigation";
import { getAuthorizedPostLoginPath } from "@/lib/permissions";
import { version } from "../../../package.json";
import { MarinaLogo } from "@/components/brand/MarinaLogo";
import { PresidenceLogo } from "@/components/brand/PresidenceLogo";
import { PrLogo } from "@/components/brand/PrLogo";

type LoginMode =
  | "choices"
  | "password"
  | "magic-link"
  | "request-password-reset"
  | "set-password";
type AuthMethods = {
  password: boolean;
  magicLink: boolean;
  ssoGovernment: boolean;
};

const ENABLE_DEV_TEST_ACCOUNTS =
  import.meta.env.VITE_ENABLE_TEST_ACCOUNTS === "true";
const USE_MULTISITE_DEMO_ACCOUNTS =
  import.meta.env.VITE_TEST_ACCOUNTS_PRESET === "multisite-demo";
const DEMO_EMAIL_DOMAIN =
  import.meta.env.VITE_DEMO_EMAIL_DOMAIN?.trim().toLowerCase() || "demo.hawk.invalid";
const DEMO_PASSWORD = import.meta.env.VITE_DEMO_PASSWORD ?? "";

type LoginTestAccount = Omit<TestAccountDefinition, "password"> & {
  password?: string;
  displayName?: string;
};

const MULTISITE_ACCOUNT_PRESENTATION: Record<
  (typeof MULTISITE_DEMO_ACCOUNTS)[number]["key"],
  Pick<LoginTestAccount, "label" | "description" | "color" | "icon">
> = {
  ADMIN: {
    label: "Administration",
    description: "Configure les référentiels et les terminaux.",
    color: "border-cyan-500/50 bg-cyan-500/5",
    icon: "Cpu",
  },
  BADGES: {
    label: "Bureau badges",
    description: "Gère l'inventaire et les badges.",
    color: "border-amber-500/50 bg-amber-500/5",
    icon: "ClipboardCheck",
  },
  "ACCUEIL-A": {
    label: "Accueil Site A",
    description: "Accueille les visiteurs du site A.",
    color: "border-emerald-500/50 bg-emerald-500/5",
    icon: "BadgeCheck",
  },
  "ACCUEIL-B": {
    label: "Accueil Site B",
    description: "Accueille les visiteurs du site B.",
    color: "border-emerald-500/50 bg-emerald-500/5",
    icon: "BadgeCheck",
  },
  HOTE: {
    label: "Hôte",
    description: "Planifie les visites et prépare les invitations.",
    color: "border-green-500/50 bg-green-500/5",
    icon: "Briefcase",
  },
  SUPERVISION: {
    label: "Supervision",
    description: "Supervise les présences et les mouvements sur les sites.",
    color: "border-violet-500/50 bg-violet-500/5",
    icon: "Radar",
  },
};

const ICON_MAP: Record<string, LucideIcon> = {
  ShieldCheck,
  ClipboardCheck,
  Users,
  UserPlus,
  BadgeCheck,
  Radar,
  Briefcase,
  Cpu,
};

const DEV_TEST_ACCOUNTS: LoginTestAccount[] = ENABLE_DEV_TEST_ACCOUNTS
  ? USE_MULTISITE_DEMO_ACCOUNTS
    ? MULTISITE_DEMO_ACCOUNTS.map((account) => ({
      ...MULTISITE_ACCOUNT_PRESENTATION[account.key],
      displayName: `${account.firstName} ${account.lastName}`,
      email: `${account.login}@${DEMO_EMAIL_DOMAIN}`,
      roleCode: account.role,
      password: DEMO_PASSWORD,
    }))
    : TEST_ACCOUNTS.filter((account) => account.visibleOnLogin === true)
  : [];

const accountsWithIcons = DEV_TEST_ACCOUNTS.map((account) => ({
  ...account,
  roleLabel: terminology.roleLabel(
    account.roleCode,
    ROLE_DEFINITIONS_BY_CODE.get(account.roleCode as CanonicalRoleCode)?.label ?? account.roleCode,
  ),
  Icon: ICON_MAP[account.icon] ?? ShieldCheck,
}));

function absoluteFrontendUrl(path: string) {
  return new URL(path, window.location.origin).toString();
}

export default function LoginPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { isAuthenticated, isLoading: isSessionLoading, user } = useAuth();
  const [methods, setMethods] = useState<AuthMethods | null>(null);
  const [mode, setMode] = useState<LoginMode>("choices");
  const [showAlternativeMethods, setShowAlternativeMethods] = useState(false);
  const [selectedAccount, setSelectedAccount] =
    useState<(typeof accountsWithIcons)[number] | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [callbackError, setCallbackError] = useState<LoginAuthError | null>(
    null,
  );
  const [notice, setNotice] = useState<string | null>(null);
  const returnTo = useMemo(
    () => getSafeLoginReturnTo(window.location.search),
    [],
  );

  useEffect(() => {
    if (!isSessionLoading && isAuthenticated) {
      setLocation(getAuthorizedPostLoginPath(user, returnTo) ?? "/dashboard");
    }
  }, [isAuthenticated, isSessionLoading, returnTo, setLocation, user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasResetToken = Boolean(params.get("token"));
    if (hasResetToken) {
      setMode("set-password");
    }
    const callbackError = resolveLoginAuthError(params);
    if (callbackError) {
      setCallbackError(callbackError);
      window.history.replaceState({}, "", buildCleanLoginUrl(params));
    }

    let active = true;
    fetch(resolveApiUrl("/api/auth/methods"), {
      credentials: "include",
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Méthodes de connexion indisponibles");
        }
        return (await response.json()) as AuthMethods;
      })
      .then((payload) => {
        if (!active) return;
        setMethods(payload);
      })
      .catch(() => {
        if (active) {
          setMethods({
            password: true,
            magicLink: false,
            ssoGovernment: false,
          });
          if (!hasResetToken && !callbackError) {
            setError(
              "Certaines méthodes de connexion sont temporairement indisponibles."
            );
          }
        }
      });
    return () => {
      active = false;
    };
  }, []);

  async function refreshApplicationSession() {
    const sessionResponse = await fetch(resolveApiUrl("/api/session"), {
      credentials: "include",
    });
    if (!sessionResponse.ok) {
      throw new Error(
        (await sessionResponse.text()) || "Session utilisateur introuvable"
      );
    }
    const sessionPayload = await sessionResponse.json();
    queryClient.setQueryData(["auth-session"], sessionPayload);
    setLocation(
      getAuthorizedPostLoginPath(sessionPayload.user ?? null, returnTo) ??
        "/dashboard",
    );
  }

  async function handlePasswordLogin(event: React.FormEvent) {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    setNotice(null);
    try {
      const result = await signIn.email({ email: email.trim(), password });
      if (result.error) {
        throw new Error("Email ou mot de passe incorrect.");
      }
      await refreshApplicationSession();
      toast({
        title: "Connexion réussie",
        description: presentation.label("Votre session Flux est active.", "Votre session Hawk est active."),
      });
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : "Échec de la connexion.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleMagicLink(event: React.FormEvent) {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    setNotice(null);
    try {
      await authClient.signIn.magicLink({
        email: email.trim(),
        callbackURL: absoluteFrontendUrl(
          buildLoginCompletionPath("magic-link", returnTo),
        ),
        errorCallbackURL: absoluteFrontendUrl(
          buildLoginErrorPath("magic-link", returnTo),
        ),
      });
      setNotice(
        "Si ce compte est autorisé, un lien de connexion vient d’être envoyé."
      );
    } catch {
      setNotice(
        "Si ce compte est autorisé, un lien de connexion vient d’être envoyé."
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function handlePasswordReset(event: React.FormEvent) {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    setNotice(null);
    try {
      await authClient.requestPasswordReset({
        email: email.trim(),
        redirectTo: absoluteFrontendUrl("/login"),
      });
    } finally {
      setNotice(
        "Si ce compte est autorisé, les instructions de réinitialisation ont été envoyées."
      );
      setIsLoading(false);
    }
  }

  async function handleSetPassword(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);

    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) {
      setError("Le lien de réinitialisation est invalide ou expiré.");
      return;
    }
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== passwordConfirmation) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await authClient.resetPassword({
        newPassword: password,
        token,
      });
      if (result.error) {
        throw new Error(
          "Le lien de réinitialisation est invalide ou expiré."
        );
      }
      window.history.replaceState({}, "", "/login");
      setPassword("");
      setPasswordConfirmation("");
      setMode("password");
      setNotice(
        "Votre mot de passe a été modifié. Vous pouvez maintenant vous connecter."
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "La réinitialisation du mot de passe a échoué."
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function handleGovernmentSso() {
    setIsLoading(true);
    setError(null);
    setNotice(null);
    try {
      const result = await authClient.signIn.oauth2({
        providerId: "gov",
        callbackURL: absoluteFrontendUrl(
          buildLoginCompletionPath("sso", returnTo),
        ),
        errorCallbackURL: absoluteFrontendUrl(
          buildLoginErrorPath("sso", returnTo),
        ),
      });
      if (result.error) {
        throw new Error("Le SSO gouvernemental n’a pas pu être démarré.");
      }
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Le SSO gouvernemental n’a pas pu être démarré."
      );
      setIsLoading(false);
    }
  }

  function retryGovernmentSso() {
    setCallbackError(null);
    void handleGovernmentSso();
  }

  function showAlternativeLogin() {
    setCallbackError(null);
    setMode("choices");
    setShowAlternativeMethods(true);
    setError(null);
    setNotice(null);
  }

  function returnToLoginChoices() {
    setCallbackError(null);
    setMode("choices");
    setError(null);
    setNotice(null);
  }

  function chooseMode(nextMode: LoginMode) {
    setMode(nextMode);
    setSelectedAccount(null);
    setError(null);
    setNotice(null);
  }

  function fillCredentials(account: (typeof accountsWithIcons)[number]) {
    setSelectedAccount(account);
    setEmail(account.email.trim());
    if (mode === "password") {
      setPassword(account.password?.trim() ?? "");
    }
    setError(null);
    setNotice(null);
  }

  const formTitle = callbackError
    ? callbackError.title
    : mode === "choices"
      ? "Connexion"
      : mode === "magic-link"
        ? "Connexion par lien unique"
        : mode === "request-password-reset"
          ? "Réinitialiser le mot de passe"
          : mode === "set-password"
            ? "Choisir un nouveau mot de passe"
            : "Connexion par email";

  const formDescription = callbackError
    ? callbackError.description
    : mode === "choices"
      ? "Choisissez votre méthode de connexion."
      : mode === "magic-link"
        ? "Recevez un lien de connexion à usage unique par email."
        : mode === "request-password-reset"
          ? "Recevez les instructions pour choisir un nouveau mot de passe."
          : mode === "set-password"
            ? presentation.label("Définissez le nouveau mot de passe de votre compte Flux.", "Définissez le nouveau mot de passe de votre compte Hawk.")
            : presentation.label("Saisissez les identifiants associés à votre compte Flux.", "Saisissez les identifiants associés à votre compte Hawk.");

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-x-hidden bg-[#f5f5f0] p-4 text-[#171714]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,#b8860b_1px,transparent_1px)] bg-[size:20px_20px] opacity-20" />

      <div className="relative z-10 my-4 grid w-full max-w-xl lg:max-w-5xl grid-cols-1 overflow-hidden rounded-2xl border border-[#b8860b]/20 bg-white shadow-2xl lg:grid-cols-2">
        <section className="relative hidden min-h-[680px] flex-col justify-between overflow-hidden bg-[#1a1a1a] p-12 text-white lg:flex">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center opacity-20 grayscale mix-blend-overlay" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#1a1a1a]/90 to-[#b8860b]/20" />

          <div className="relative z-10 flex flex-col items-center gap-2 text-center">
            {presentation.isGeneric ? <img src={hawkLogo} alt="Hawk" className="h-24 w-auto" /> : <MarinaLogo mask={false} className="h-24 w-auto" />}

            <h1 className="font-display text-4xl font-bold">{presentation.label("FLUX & VISITES", "HAWK")}</h1>
            <p className="mt-2 font-mono text-sm uppercase tracking-widest text-[#b8860b]">
              {presentation.organizationName}
            </p>
          </div>

          <div className="relative z-10 space-y-6">
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-sm font-medium text-white/80">
                <ShieldUser className="h-5 w-5 text-[#b8860b]" />
                <span>{presentation.label("Identité Flux vérifiée", "Gestion des visiteurs et des accès")}</span>
              </div>
              <p className="pl-8 text-xs leading-5 text-gray-500">
                Chaque accès reste lié à une personne et à un rôle actif.
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-sm font-medium text-white/80">
                <KeyRound className="h-5 w-5 text-[#b8860b]" />
                <span>Contrôle d’accès unifié</span>
              </div>
              <p className="pl-8 text-xs leading-5 text-gray-500">
                Mot de passe, lien unique et SSO partagent les mêmes droits
                d'accès.
              </p>
            </div>
          </div>

          <p
            className="relative z-10 font-mono text-xs text-gray-700 select-none"
            title={presentation.label("Kisso Industries - Flux", "Kisso Industries - Hawk")}
          >
            {presentation.label("Kisso Industries - Flux", "Kisso Industries - Hawk")}
          </p>
        </section>

        <section className="flex flex-col justify-center bg-[#f5f5f0] pb-4 sm:pb-6 lg:pb-8">
          <div className="flex flex-col justify-center p-7 sm:p-9 lg:p-12">
            <div className="mb-6 flex flex-col items-center justify-center gap-3">
              {presentation.isGeneric ? <img src={hawkLogo} alt="Hawk" className="h-18 w-auto" /> : <PresidenceLogo className="h-18 w-auto" />}
              {presentation.isGeneric ? <p className="text-center text-xs font-medium uppercase tracking-widest text-muted-foreground">{presentation.organizationName}</p> : null}
            </div>

            <div className="mb-7 text-center ">
              <h2 className="font-display text-2xl font-bold">{formTitle}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {formDescription}
              </p>
            </div>

            {error && !callbackError ? (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2 rounded border border-red-200/50 bg-red-500/10 p-3 text-xs text-red-600"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            ) : null}

            {notice ? (
              <div
                role="status"
                className="mb-4 flex items-start gap-2 rounded border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{notice}</span>
              </div>
            ) : null}

            {methods === null && !callbackError ? (
              <div
                role="status"
                className="flex h-24 items-center justify-center text-sm text-muted-foreground"
              >
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-[#b8860b]/30 border-t-[#b8860b]" />
                Chargement des méthodes de connexion…
              </div>
            ) : null}

            {!callbackError &&
            mode !== "choices" &&
            mode !== "set-password" ? (
              <button
                type="button"
                className="mb-6 inline-flex w-fit items-center gap-2 text-xs font-semibold text-[#80600d] transition-colors hover:text-[#5d4509] hover:underline"
                onClick={() =>
                  chooseMode(
                    mode === "request-password-reset" ? "password" : "choices",
                  )
                }
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {mode === "request-password-reset"
                  ? "Retour à la connexion"
                  : "Toutes les méthodes"}
              </button>
            ) : null}

            {callbackError ? (
              <div className="space-y-5" data-testid="sso-error-view">
                <div
                  role="alert"
                  className="rounded-lg border border-red-200/70 bg-red-50/80 p-4 text-sm text-red-950"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-red-100 text-red-700">
                      <AlertCircle className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div className="space-y-2">
                      <p className="font-semibold">Que faire maintenant ?</p>
                      <p className="leading-5 text-red-900/80">
                        {callbackError.shouldContactSupport
                          ? "Contactez Assistance Plateforme depuis le bouton « Contact utile » en bas de la page."
                          : presentation.label("Vous pouvez reprendre la connexion depuis Flux sans réutiliser l’ancien onglet.", "Vous pouvez reprendre la connexion depuis Hawk sans réutiliser l’ancien onglet.")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3">
                  {callbackError.canRetrySso && methods?.ssoGovernment ? (
                    <Button
                      type="button"
                      className="h-12 w-full bg-[#11344f] text-white hover:bg-[#0b2941]"
                      onClick={retryGovernmentSso}
                      disabled={isLoading}
                    >
                      {isLoading
                        ? "Redirection en cours…"
                        : "Réessayer avec le compte gouvernemental"}
                    </Button>
                  ) : null}
                  {callbackError.canUseAlternative &&
                  (methods?.password || methods?.magicLink) ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-12 w-full bg-white/70"
                      onClick={showAlternativeLogin}
                    >
                      Utiliser une connexion alternative
                    </Button>
                  ) : null}
                  <button
                    type="button"
                    className="mx-auto inline-flex items-center gap-2 py-1 text-xs font-semibold text-[#80600d] hover:underline"
                    onClick={returnToLoginChoices}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                    Retour à la connexion
                  </button>
                </div>
              </div>
            ) : null}

            {!callbackError && mode === "choices" && methods ? (
              <>
                <div className="grid gap-3">
                  {methods.ssoGovernment ? (
                    <Button
                      type="button"
                      className="relative h-16 w-full justify-center bg-[#11344f] px-12 text-white shadow-md shadow-[#11344f]/15 hover:bg-[#0b2941]"
                      onClick={handleGovernmentSso}
                      disabled={isLoading}
                    >
                      <PrLogo className="absolute left-4 h-12 w-12" />
                      {isLoading ? (
                        "Redirection en cours…"
                      ) : (
                        <span className="flex flex-col items-center leading-tight">
                          <span className="text-base font-semibold">
                            Cliquez ici pour vous connecter
                          </span>
                          <span className="mt-1 text-[11px] font-normal text-white/70">
                            avec votre compte gouvernemental
                          </span>
                        </span>
                      )}
                    </Button>
                  ) : null}
                  {methods.password || methods.magicLink ? (
                    <>
                      <button
                        type="button"
                        className={cn(
                          "group mx-auto inline-flex items-center gap-1 py-1 text-[11px] font-normal text-muted-foreground/70 underline-offset-4 transition-colors hover:text-muted-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b8860b]/30 focus-visible:ring-offset-2",
                          methods.ssoGovernment && "mt-7",
                        )}
                        aria-expanded={showAlternativeMethods}
                        aria-controls="alternative-login-methods"
                        onClick={() =>
                          setShowAlternativeMethods((isVisible) => !isVisible)
                        }
                      >
                        Connexion alternative
                        <ChevronDown
                          className={cn(
                            "h-3.5 w-3.5 transition-transform duration-300 ease-in-out motion-reduce:transition-none",
                            showAlternativeMethods && "rotate-180",
                          )}
                          aria-hidden="true"
                        />
                      </button>

                      <div
                        id="alternative-login-methods"
                        className={cn(
                          "grid transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none",
                          showAlternativeMethods
                            ? "grid-rows-[1fr] opacity-100"
                            : "grid-rows-[0fr] opacity-0",
                        )}
                        aria-hidden={!showAlternativeMethods}
                      >
                        <div className="overflow-hidden">
                          <div className="grid gap-3 pt-1">
                            {methods.password ? (
                              <Button
                                type="button"
                                variant="outline"
                                className="relative h-14 w-full justify-center border-black/15 bg-white/65 px-12 text-base text-[#171714] hover:border-[#b8860b]/40 hover:bg-white hover:text-[#171714]"
                                onClick={() => chooseMode("password")}
                                tabIndex={showAlternativeMethods ? 0 : -1}
                              >
                                <Mail className="absolute left-4 h-5 w-5" />
                                Connexion par email
                              </Button>
                            ) : null}
                            {methods.magicLink ? (
                              <Button
                                type="button"
                                variant="outline"
                                className="relative h-14 w-full justify-center border-black/15 bg-white/65 px-12 text-base text-[#171714] hover:border-[#b8860b]/40 hover:bg-white hover:text-[#171714]"
                                onClick={() => chooseMode("magic-link")}
                                tabIndex={showAlternativeMethods ? 0 : -1}
                              >
                                <Link2 className="absolute left-4 h-5 w-5" />
                                Connexion par lien unique
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </>
            ) : null}

            {!callbackError && mode === "password" && methods?.password ? (
              <form onSubmit={handlePasswordLogin} className="space-y-4">
                <EmailField email={email} setEmail={setEmail} />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Mot de passe</Label>
                    <button
                      type="button"
                      className="text-xs text-[#b8860b] hover:underline"
                      onClick={() => {
                        chooseMode("request-password-reset");
                      }}
                    >
                      Oublié ?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className="h-11 bg-white pl-9"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                    />
                  </div>
                </div>
                <PrimarySubmit loading={isLoading}>Se connecter</PrimarySubmit>
              </form>
            ) : null}

            {!callbackError && mode === "magic-link" && methods?.magicLink ? (
              <form onSubmit={handleMagicLink} className="space-y-4">
                <EmailField email={email} setEmail={setEmail} />
                <PrimarySubmit loading={isLoading}>
                  Envoyer le lien unique
                </PrimarySubmit>
              </form>
            ) : null}

            {!callbackError && mode === "request-password-reset" ? (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <EmailField email={email} setEmail={setEmail} />
                <PrimarySubmit loading={isLoading}>
                  Envoyer les instructions
                </PrimarySubmit>
              </form>
            ) : null}

            {!callbackError && mode === "set-password" ? (
              <form onSubmit={handleSetPassword} className="space-y-4">
                <PasswordField
                  id="new-password"
                  label="Nouveau mot de passe"
                  autoComplete="new-password"
                  value={password}
                  onChange={setPassword}
                />
                <PasswordField
                  id="confirm-password"
                  label="Confirmer le mot de passe"
                  autoComplete="new-password"
                  value={passwordConfirmation}
                  onChange={setPasswordConfirmation}
                />
                <PrimarySubmit loading={isLoading}>
                  Enregistrer le mot de passe
                </PrimarySubmit>
              </form>
            ) : null}

            {/* <div className="mt-6 gap-2 rounded border border-blue-200/50 bg-blue-500/5 p-2 text-xs text-muted-foreground">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
              <p className="text-center">
                Système surveillé. <br /> Tout accès non autorisé est interdit
                et enregistré.
              </p>
            </div> */}

            {!callbackError &&
            (mode === "password" || mode === "magic-link") &&
            ENABLE_DEV_TEST_ACCOUNTS &&
            accountsWithIcons.length ? (
              <div className="mt-6 border-t border-dashed border-black/15 pt-5">
                <p className="mb-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {USE_MULTISITE_DEMO_ACCOUNTS ? "Accès rapides" : "Comptes de démonstration"}
                </p>
                {USE_MULTISITE_DEMO_ACCOUNTS && mode === "password" ? (
                  <p className="mb-3 text-xs text-muted-foreground">
                    Sélectionnez un compte pour remplir ses identifiants.
                  </p>
                ) : null}
                <TooltipProvider delayDuration={150}>
                  <div className={cn(
                    "grid gap-2",
                    USE_MULTISITE_DEMO_ACCOUNTS ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2 lg:grid-cols-3",
                  )}>
                    {accountsWithIcons.map((account) => (
                      <Tooltip key={account.email}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => fillCredentials(account)}
                            className={cn(
                              "flex min-h-14 items-center gap-2 rounded-lg border bg-white/60 p-2 text-left transition-all hover:bg-white",
                              selectedAccount?.email === account.email
                                ? "border-[#b8860b]/60 ring-1 ring-[#b8860b]/50 shadow-sm"
                                : "border-border/40 hover:border-border",
                            )}
                          >
                            <span
                              className={cn(
                                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                                account.color,
                              )}
                            >
                              <account.Icon className="h-3.5 w-3.5 opacity-80" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className={cn("block text-xs font-semibold", account.displayName ? "break-words" : "truncate")}>
                                {account.displayName ?? account.label}
                              </span>
                              {account.displayName ? (
                                <span className="block text-[10px] text-muted-foreground">
                                  {account.label}
                                </span>
                              ) : null}
                              <span className="block truncate text-[10px] text-muted-foreground">
                                {account.email}
                              </span>
                            </span>
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {account.roleLabel} — {account.description}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </TooltipProvider>
              </div>
            ) : null}
          </div>
          <div
            className="flex gap-2 items-center self-center select-none"
            title={presentation.label("Kisso Industries - Flux", "Kisso Industries - Hawk")}
          >
            <img
              src={hawkLogo}
              alt={presentation.productName}
              draggable={false}
              className=" h-6 w-6 object-contain drop-shadow-[0_0_10px_rgba(184,134,11,0.3)] select-none"
            />
            <p className="relative z-10 font-mono text-xs text-gray-600">
              v{version}
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}

function EmailField({
  email,
  setEmail,
}: {
  email: string;
  setEmail: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor="login-email">Adresse email</Label>
      <div className="relative">
        <Mail className="absolute left-3 top-3 h-4 w-4 text-black/35" />
        <Input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder={presentation.label("nom@presidence.bj", "nom@organisation.bj")}
          className="h-11 bg-white pl-9"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
      </div>
    </div>
  );
}

function PasswordField({
  id,
  label,
  autoComplete,
  value,
  onChange,
}: {
  id: string;
  label: string;
  autoComplete: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          id={id}
          type="password"
          autoComplete={autoComplete}
          minLength={8}
          className="h-11 bg-white pl-9"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required
        />
      </div>
    </div>
  );
}

function PrimarySubmit({
  loading,
  children,
}: {
  loading: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="submit"
      className="h-12 w-full bg-[#b8860b] text-white shadow-lg shadow-[#b8860b]/15 hover:bg-[#8b6910]"
      disabled={loading}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          Traitement…
        </span>
      ) : (
        <span className="flex items-center gap-2">
          {children}
          <ArrowRight className="h-4 w-4" />
        </span>
      )}
    </Button>
  );
}
